import { useCallback, useMemo, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { EditContentHeader } from "../../components/ui/edit-content-header";
import { classNames } from "primereact/utils";
import { useToast } from "../../components/ui/toast-context-provider";
import { Treatment } from "../../queries/models/treatment.model";
import {
  useActiveTreatmentTypesAsSelectOptionQuery,
  useTreatmentTypeQuery,
} from "../../queries/veterinary-treatment-types.query";
import FormikCalendar from "../../components/ui/formik/FormikCalendar";
import FormikDropDown from "../../components/ui/formik/FormikDropdown";
import { useActiveHorsesAsSelectOptionQuery } from "../../queries/horses.query";
import { HorseTreatmentsSection } from "./HorseTreatmentsSection";
import FormikInputText from "../../components/ui/formik/FormikInputText";
import { FileUpload } from "../../components/ui/file-upload";
import { FileInfo } from "../../utils/file-info";
import FormikImageFileUpload from "../../components/ui/formik/FormikFilesUpload";
import FormikInputTextarea from "../../components/ui/formik/FormikInputTextarea";
import { FileDownload } from "../../components/ui/FileDownload";
import { Panel } from "primereact/panel";
import { FileDelete } from "../../components/ui/FileDelete";
import { useQueryClient } from "react-query";

export interface TreatmentEditFormProps {
  treatment: Treatment;
  isSubmitting?: boolean;
  onSave: (form: Treatment) => Promise<any>;
  onCancel: () => Promise<any> | void;
  onDelete: () => Promise<any> | void;
}

export function TreatmentsForm({
  treatment,
  onSave,
  onCancel,
  onDelete,
}: TreatmentEditFormProps) {
  const lg = false;
  const toast = useToast();
  const typesQuery = useActiveTreatmentTypesAsSelectOptionQuery();
  const horsesQuery = useActiveHorsesAsSelectOptionQuery();
  const [existingFiles, setExistingFiles] = useState(treatment.files ?? []);
  const hasWriteAccess = true;

  const horseOptions = useMemo(() => {
    return (horsesQuery.data ?? [])
      .filter((h) => h.label && h.value !== undefined)
      .map((h) => ({
        label: h.label ?? "",
        value: h.value!,
      }));
  }, [horsesQuery.data]);

  const typeOptions = useMemo(() => {
    return (typesQuery.data ?? [])
      .filter((t) => t.label && t.value !== undefined)
      .map((t) => ({
        label: t.label ?? "",
        value: t.value!,
      }));
  }, [typesQuery.data]);

  const initialValues: Treatment = {
    id: treatment?.id ?? 0,
    comment: treatment?.comment ?? "",
    date: treatment?.date ? new Date(treatment.date) : new Date(),
    treatmentTypeId: treatment?.treatmentTypeId ?? undefined,
    horseTreatments: treatment?.horseTreatments ?? [],
    newFiles: [],
  };

  const validationSchema = Yup.object({
    date: Yup.date().required(),
    treatmentTypeId: Yup.number().required(),
    comment: Yup.string().optional().nullable(),
    horseTreatments: Yup.array()
      .of(
        Yup.object().shape({
          horseId: Yup.number().required(),
          comment: Yup.string().nullable(),
        })
      )
      .required(),
  });

  const onSubmit = useCallback(
    (values: Treatment) => {
      return onSave(values);
    },
    [onSave]
  );

  return (
    <div className="p-2 h-full w-full">
      {true && (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
          validateOnChange
          validateOnMount
        >
          {(formik) => (
            <Form
              autoComplete="off"
              className="flex flex-column h-full w-full overflow-hidden"
            >
              <div className="formgrid grid mb-2 overflow-y-auto overflow-x-hidden h-full">
                <div className="field col-12 md:col-12">
                  <FormikCalendar
                    label="Data"
                    name="date"
                    validationSchema={validationSchema}
                    locale="pl"
                    dateFormat="DD dd MM yy"
                    showTime={true}
                    disabled={!hasWriteAccess}
                  />
                </div>
                <div className="field col-12 md:col-6">
                  <FormikDropDown
                    label="Typ zabiegu"
                    name="treatmentTypeId"
                    validationSchema={validationSchema}
                    options={typeOptions}
                    disabled={!hasWriteAccess}
                  />
                </div>
                <div className="field col-12 md:col-6">
                  <FormikInputTextarea
                    label="Komentarz"
                    name="comment"
                    validationSchema={validationSchema}
                    disabled={!hasWriteAccess}
                  />
                </div>

                <div className="field col-12">
                  <Panel header="Zachowane pliki">
                    {existingFiles.map((x) => (
                      <div
                        key={x.id}
                        className="flex flex-row justify-content-space-between border-bottom-1 border-300 p-2"
                      >
                        <div>
                          <FileDownload file={x} />
                        </div>
                        <div>
                          <FileDelete
                            fileId={x.id}
                            onDelete={(fileId: number) => {
                              setExistingFiles((prev) =>
                                prev.filter((f) => f.id !== fileId)
                              );
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </Panel>
                </div>

                <div className="field col-12">
                  <FormikImageFileUpload
                    className="w-full"
                    validationSchema={validationSchema}
                    label="Dodaj nowe pliki"
                    name="newFiles"
                    multiple={true}
                    imageOnly
                  />
                </div>
                {horsesQuery.data && (
                  <div className="flex w-full flex-column p-2">
                    <div className="flex justify-content-between align-items-center w-full mb-2">
                      <div className="text-left">
                        <label className="font-medium">
                          Konie ({formik.values.horseTreatments?.length})
                        </label>
                      </div>
                      <div className="text-right">
                        <button
                          type="button"
                          className="p-button p-button-sm p-button-text"
                          onClick={() => {
                            const allSelected =
                              formik.values.horseTreatments?.length ===
                              horseOptions.length;
                            const newSelection = allSelected
                              ? []
                              : horseOptions.map((h) => ({
                                  horseId: h.value,
                                  comment: "",
                                }));
                            formik.setFieldValue(
                              "horseTreatments",
                              newSelection
                            );
                          }}
                        >
                          {formik.values.horseTreatments?.length ===
                          horseOptions.length
                            ? "Odznacz wszystkie"
                            : "Zaznacz wszystkie"}
                        </button>
                      </div>
                    </div>

                    <HorseTreatmentsSection horses={horseOptions} />
                  </div>
                )}
              </div>

              <EditContentHeader
                header={
                  treatment?.id
                    ? treatment.date.toLocaleString()
                    : "Dodaj zabieg"
                }
                showDeleteButton={hasWriteAccess && !!treatment.id}
                showCancelButton={false}
                saveButtonDisabled={
                  !hasWriteAccess ||
                  formik.isSubmitting ||
                  !formik.values.treatmentTypeId ||
                  formik.values.horseTreatments?.length === 0
                }
                onSaveClick={async () => {
                  if (!formik.isValid) {
                    toast.current?.show({
                      severity: "error",
                      detail: "Form invalid",
                    });
                    return;
                  }
                  return formik.submitForm();
                }}
                onCancelClick={onCancel}
                onDeleteClick={onDelete}
              />
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}
